import React, { useState, useEffect } from 'react';
import {
  MDBCard,
  MDBCardBody,
  MDBTabs,
  MDBTabsItem,
  MDBTabsLink,
  MDBTable,
  MDBTableHead,
  MDBTableBody,
  MDBIcon,
  MDBInput,
  MDBSpinner,
  MDBRow,
  MDBCol,
  MDBTabsContent,
  MDBTabsPane,
  MDBBtn,
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBBadge
} from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import { generateClient } from 'aws-amplify/api';
import * as XLSX from 'xlsx';
import shortUUID from 'short-uuid';
import { getLocalizedText } from "../../utils/localizedText";

const client = generateClient();

export default function AdminEventInvitationCodes() {
  const { t, i18n } = useTranslation();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [invitationCodes, setInvitationCodes] = useState({});
  const [codesUsage, setCodesUsage] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [newCodes, setNewCodes] = useState({});
  const [discountModal, setDiscountModal] = useState({ open: false, editMode: false, discountCodeId: null, invitationCodeId: null, companyId: null, companyName: '', code: '' });
  const [discountForm, setDiscountForm] = useState({ maxUsage: 5 });
  const [isSavingDiscount, setIsSavingDiscount] = useState(false);
  const [sortBy, setSortBy] = useState('name'); // 'name' or 'invited'
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');

  // Load initial events
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const cutoffDate = thirtyDaysAgo.toISOString().split('T')[0];

        const eventsData = await client.graphql({ // , hasInvitationCodes: { eq: true }
          query: /* GraphQL */ `
            query ListEvents($filter: ModelEventFilterInput) {
              listEvents(filter: $filter) {
                items {
                  id
                  name {
                    language
                    text
                  }
                  startDate
                  endDate
                }
              }
            }
          `,
          variables: {
            filter: {
              type: { eq: "fair" },
              endDate: { ge: cutoffDate }
            }
          }
        });
        const sortedEvents = eventsData.data.listEvents.items.sort((a, b) => 
          new Date(b.startDate) - new Date(a.startDate)
        );
        setEvents(sortedEvents);
        if (sortedEvents.length > 0) {
          setSelectedEvent(sortedEvents[0].id);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
      }
    };
    fetchEvents();
  }, []);

  // Load companies with booths and their codes when event is selected
  useEffect(() => {
    if (!selectedEvent) return;

    const fetchCompaniesWithBooths = async () => {
      setIsLoading(true);
      try {
        let allBookedBooths = [];
        let nextToken = null;

        do {
        const response = await client.graphql({
            query: /* GraphQL */ `
            query ListBookedBooths($filter: ModelBookedBoothFilterInput, $nextToken: String) {
                listBookedBooths(filter: $filter, nextToken: $nextToken) {
                items {
                    company {
                    id
                    name
                    image {
                        main {
                        fileName
                        }
                    }
                    }
                    orderLineItem {
                        order {
                        deliveryAddress {
                            company
                            city
                            contactPerson
                            countryCode
                            firstName
                            lastName
                            street
                            streetNumber
                            street2
                            zip
                        }
                        user {
                            firstName
                            lastName
                            salutation
                        }
                        }
                    }
                }
                nextToken
                }
            }
            `,
            variables: {
            filter: {
                eventBookedBoothsId: { eq: selectedEvent }
            },
            nextToken: nextToken
            }
        });

        allBookedBooths = allBookedBooths.concat(response.data.listBookedBooths.items);
        nextToken = response.data.listBookedBooths.nextToken;
        } while (nextToken);
        
        // Transform the data structure and remove duplicates
        const companiesWithBooths = allBookedBooths
          .map(item => ({...item.company, orderLineItem: item.orderLineItem}))
          .filter((company, index, self) => 
            index === self.findIndex((c) => c.id === company.id)
          )
            .sort((a, b) => a.name.localeCompare(b.name));
        
        setCompanies(companiesWithBooths);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching companies with booths:', error);
        setIsLoading(false);
      }
    };
    fetchCompaniesWithBooths();
  }, [selectedEvent]);

  // Load invitation codes separately with pagination
  useEffect(() => {
    if (!selectedEvent) return;

    const fetchInvitationCodes = async () => {
      try {
        let allCodes = [];
        let nextToken = null;

        do {
          const response = await client.graphql({
            query: /* GraphQL */ `
              query ListInvitationCodes($filter: ModelInvitationCodeFilterInput, $nextToken: String, $limit: Int) {
                listInvitationCodes(filter: $filter, nextToken: $nextToken, limit: $limit) {
                  items {
                    id
                    code
                    companyInvitationCodesId
                    discountCode {
                      id
                      code
                      discountPercentage
                      maxUsage
                      usageCount
                    }
                  }
                  nextToken
                }
              }
            `,
            variables: {
              filter: {
                eventInvitationCodesId: { eq: selectedEvent }
              },
              limit: 1000,
              nextToken
            }
          });

          allCodes = allCodes.concat(response.data.listInvitationCodes.items);
          nextToken = response.data.listInvitationCodes.nextToken;
        } while (nextToken);

        // Group codes by company
        const codesByCompany = {};
        allCodes.forEach(code => {
          if (!codesByCompany[code.companyInvitationCodesId]) {
            codesByCompany[code.companyInvitationCodesId] = [];
          }
          codesByCompany[code.companyInvitationCodesId].push(code);
        });

        setInvitationCodes(codesByCompany);
      } catch (error) {
        console.error('Error fetching invitation codes:', error);
      }
    };

    fetchInvitationCodes();
  }, [selectedEvent]);

  // Load all ticket usage in one searchTickets call
  useEffect(() => {
    if (!selectedEvent) return;

    const fetchAllTicketUsage = async () => {
      try {
        const usageData = {};
        let nextToken = null;

        do {
          const response = await client.graphql({
            query: /* GraphQL */ `
              query SearchTickets($filter: SearchableTicketFilterInput, $nextToken: String, $limit: Int) {
                searchTickets(filter: $filter, nextToken: $nextToken, limit: $limit) {
                  items {
                    invitationCode
                  }
                  nextToken
                  total
                }
              }
            `,
            variables: {
              filter: {
                ticketEventId: { eq: selectedEvent },
                invitationCode: { exists: true }
              },
              limit: 1000,
              nextToken
            }
          });

          response.data.searchTickets.items.forEach(ticket => {
            if (ticket.invitationCode) {
              usageData[ticket.invitationCode] = (usageData[ticket.invitationCode] || 0) + 1;
            }
          });
          nextToken = response.data.searchTickets.nextToken;
        } while (nextToken);

        setCodesUsage(usageData);
      } catch (error) {
        console.error('Error fetching ticket usage:', error);
      }
    };

    fetchAllTicketUsage();
  }, [selectedEvent]);

  const handleAddCode = async (companyId, code) => {
    try {
      const response = await client.graphql({
        query: /* GraphQL */ `
          mutation CreateInvitationCode($input: CreateInvitationCodeInput!) {
            createInvitationCode(input: $input) {
              id
              code
            }
          }
        `,
        variables: {
          input: {
            code: code.toUpperCase(),
            createdByAdmin: true,
            companyInvitationCodesId: companyId,
            eventInvitationCodesId: selectedEvent
          }
        }
      });

      // Update local state
      setInvitationCodes(prev => ({
        ...prev,
        [companyId]: [...(prev[companyId] || []), response.data.createInvitationCode]
      }));

      // Clear new code input
      setNewCodes(prev => {
        const newCodes = {...prev};
        delete newCodes[companyId];
        return newCodes;
      });
    } catch (error) {
      console.error('Error adding code:', error);
    }
  };

  const handleDeleteCode = async (companyId, codeId) => {
    try {
      await client.graphql({
        query: /* GraphQL */ `
          mutation DeleteInvitationCode($input: DeleteInvitationCodeInput!) {
            deleteInvitationCode(input: $input) {
              id
            }
          }
        `,
        variables: {
          input: {
            id: codeId
          }
        }
      });

      // Update local state
      setInvitationCodes(prev => ({
        ...prev,
        [companyId]: (prev[companyId] || []).filter(code => code.id !== codeId)
      }));
    } catch (error) {
      console.error('Error deleting code:', error);
    }
  };

  const handleDownloadExcel = () => {
    // Daten für Excel vorbereiten
    const excelData = [];
    
    // Header hinzufügen
    excelData.push(['Company', 'Code', 'Usage', "Admin_Anrede", "Admin_Vorname", "Admin_Nachname", "Adresse_Firma", "Straße", "Hausnummer", "Straße_2", "PLZ", "Ort", "Land"])
    
    // Daten für jede Firma hinzufügen
    companies.forEach(company => {
      getCompanyCodes(company.id).forEach(code => {
        excelData.push([
          company.name,
          code.code,
          codesUsage[code.code] || 0,
            company.orderLineItem?.order?.user?.salutation || "",
            company.orderLineItem?.order?.user?.firstName || "",
            company.orderLineItem?.order?.user?.lastName || "",
            company.orderLineItem?.order?.deliveryAddress?.company || company.name,
            company.orderLineItem?.order?.deliveryAddress?.street || "",
            company.orderLineItem?.order?.deliveryAddress?.streetNumber || "",
            company.orderLineItem?.order?.deliveryAddress?.street2 || "",
            company.orderLineItem?.order?.deliveryAddress?.zip || "",
            company.orderLineItem?.order?.deliveryAddress?.city || "",
            company.orderLineItem?.order?.deliveryAddress?.countryCode || ""
        ]);
      });
    });

    // Arbeitsmappe erstellen
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    // Spaltenbreiten automatisch anpassen
    const colWidths = [
      { wch: 30 },  // Company
      { wch: 15 },  // Code
      { wch: 10 }   // Usage
    ];
    ws['!cols'] = colWidths;

    // Worksheet zur Arbeitsmappe hinzufügen
    XLSX.utils.book_append_sheet(wb, ws, 'Invitation Codes');

    // Excel-Datei herunterladen
    XLSX.writeFile(wb, `Einladungscodes-${getLocalizedText(events.find(e => e.id === selectedEvent).name)}.xlsx`);
  };

  const generateAndAddCodes = async (companyId) => {
    console.log("generateAndAddCodes", companyId);
    let companiesToAddCodesTo = [];
    if (companyId) {
        companiesToAddCodesTo = companies.filter(company => company.id === companyId);
    } else {
        companiesToAddCodesTo = companies.filter(
      company => !getCompanyCodes(company.id).length
    );
    }
    console.log(companiesToAddCodesTo);
    for (const company of companiesToAddCodesTo) {
      const newCode = shortUUID().new().substring(0, 6).toUpperCase();
      try {
        await handleAddCode(company.id, newCode);
      } catch (error) {
        console.error(`Error adding code for company ${company.name}:`, error);
      }
    }
  };

  const handleLinkDiscount = async () => {
    setIsSavingDiscount(true);
    try {
      // Create a new DiscountCode with 100% discount
      const createResponse = await client.graphql({
        query: /* GraphQL */ `
          mutation CreateDiscountCode($input: CreateDiscountCodeInput!) {
            createDiscountCode(input: $input) {
              id
              code
              discountPercentage
              maxUsage
              usageCount
            }
          }
        `,
        variables: {
          input: {
            code: discountModal.code,
            name: [
              { language: 'de', text: `Einladungsrabatt ${discountModal.companyName}` },
              { language: 'en', text: `Invitation discount ${discountModal.companyName}` }
            ],
            discountType: 'percentage',
            discountPercentage: 100,
            maxUsage: discountForm.maxUsage,
            usageCount: 0,
            status: 'active',
            canBeCombined: false,
            type: 'invitationDiscount'
          }
        }
      });

      const newDiscountCode = createResponse.data.createDiscountCode;

      // Link to InvitationCode
      await client.graphql({
        query: /* GraphQL */ `
          mutation UpdateInvitationCode($input: UpdateInvitationCodeInput!) {
            updateInvitationCode(input: $input) {
              id
            }
          }
        `,
        variables: {
          input: {
            id: discountModal.invitationCodeId,
            invitationCodeDiscountCodeId: newDiscountCode.id
          }
        }
      });

      // Update local state
      setInvitationCodes(prev => ({
        ...prev,
        [discountModal.companyId]: (prev[discountModal.companyId] || []).map(ic =>
          ic.id === discountModal.invitationCodeId
            ? { ...ic, discountCode: newDiscountCode }
            : ic
        )
      }));

      setDiscountModal({ open: false, editMode: false, discountCodeId: null, invitationCodeId: null, companyId: null, companyName: '', code: '' });
      setDiscountForm({ maxUsage: 5 });
    } catch (error) {
      console.error('Error linking discount code:', error);
    }
    setIsSavingDiscount(false);
  };

  const handleUpdateDiscount = async () => {
    setIsSavingDiscount(true);
    try {
      await client.graphql({
        query: /* GraphQL */ `
          mutation UpdateDiscountCode($input: UpdateDiscountCodeInput!) {
            updateDiscountCode(input: $input) {
              id
              maxUsage
            }
          }
        `,
        variables: {
          input: {
            id: discountModal.discountCodeId,
            maxUsage: discountForm.maxUsage
          }
        }
      });

      // Update local state
      setInvitationCodes(prev => ({
        ...prev,
        [discountModal.companyId]: (prev[discountModal.companyId] || []).map(ic =>
          ic.id === discountModal.invitationCodeId
            ? { ...ic, discountCode: { ...ic.discountCode, maxUsage: discountForm.maxUsage } }
            : ic
        )
      }));

      setDiscountModal({ open: false, editMode: false, discountCodeId: null, invitationCodeId: null, companyId: null, companyName: '', code: '' });
      setDiscountForm({ maxUsage: 5 });
    } catch (error) {
      console.error('Error updating discount code:', error);
    }
    setIsSavingDiscount(false);
  };

  const handleUnlinkDiscount = async (companyId, invitationCodeId, discountCodeId) => {
    try {
      // Unlink from InvitationCode
      await client.graphql({
        query: /* GraphQL */ `
          mutation UpdateInvitationCode($input: UpdateInvitationCodeInput!) {
            updateInvitationCode(input: $input) {
              id
            }
          }
        `,
        variables: {
          input: {
            id: invitationCodeId,
            invitationCodeDiscountCodeId: null
          }
        }
      });

      // Delete the discount code since it only exists for this invitation code
      await client.graphql({
        query: /* GraphQL */ `
          mutation DeleteDiscountCode($input: DeleteDiscountCodeInput!) {
            deleteDiscountCode(input: $input) {
              id
            }
          }
        `,
        variables: {
          input: { id: discountCodeId }
        }
      });

      // Update local state
      setInvitationCodes(prev => ({
        ...prev,
        [companyId]: (prev[companyId] || []).map(ic =>
          ic.id === invitationCodeId
            ? { ...ic, discountCode: null }
            : ic
        )
      }));
    } catch (error) {
      console.error('Error unlinking discount code:', error);
    }
  };

  const getCompanyCodes = (companyId) => invitationCodes[companyId] || [];

  const getCompanyInvitedTotal = (company) => {
    return getCompanyCodes(company.id).reduce(
      (sum, code) => sum + (codesUsage[code.code] || 0), 0
    );
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection(column === 'invited' ? 'desc' : 'asc');
    }
  };

  const filteredAndSortedCompanies = companies
    .filter(company => company.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      const dir = sortDirection === 'asc' ? 1 : -1;
      if (sortBy === 'invited') {
        return dir * (getCompanyInvitedTotal(a) - getCompanyInvitedTotal(b));
      }
      return dir * a.name.localeCompare(b.name);
    });

  return (
    <MDBCard>
      <MDBCardBody>
        <MDBTabs className='mb-3'>
          {events.map((event) => (
            <MDBTabsItem key={event.id}>
              <MDBTabsLink
                onClick={() => setSelectedEvent(event.id)}
                active={selectedEvent === event.id}
              >
                {event.name.find(n => n.language === i18n.language)?.text || event.name[0].text}
              </MDBTabsLink>
            </MDBTabsItem>
          ))}
        </MDBTabs>
        <div className="d-flex gap-2 mb-3">
          <MDBBtn
            color="primary"
            onClick={handleDownloadExcel}
            disabled={isLoading || companies.length === 0}
          >
            <MDBIcon fas icon="file-excel" className="me-2" />
            {t("Download")}
          </MDBBtn>
          <MDBBtn
            color="success"
            onClick={() => generateAndAddCodes()}
            disabled={isLoading || companies.length === 0}
          >
            <MDBIcon fas icon="magic" className="me-2" />
            {t("Generate Missing Codes")}
          </MDBBtn>
        </div>
        <div className="mb-3" style={{ maxWidth: '300px' }}>
          <MDBInput
            label={t("Search company")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <MDBTabsContent>
          <MDBTabsPane open={true}>
            {isLoading ? (
              <div className="text-center">
                <MDBSpinner />
              </div>
            ) : (
              <MDBTable>
                <MDBTableHead>
                  <tr>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>
                      {t("Company")}
                      {sortBy === 'name' && <MDBIcon fas icon={sortDirection === 'asc' ? 'sort-alpha-down' : 'sort-alpha-up'} className="ms-1" />}
                    </th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('invited')}>
                      {t("Invited")}
                      {sortBy === 'invited' && <MDBIcon fas icon={sortDirection === 'asc' ? 'sort-amount-up' : 'sort-amount-down'} className="ms-1" />}
                    </th>
                    <th>{t("Invitation Codes")}</th>
                  </tr>
                </MDBTableHead>
                <MDBTableBody>
                  {filteredAndSortedCompanies.map((company) => (
                    <tr key={company.id}>
                      <td className="align-middle">
                        <div className="d-flex align-items-center">
                          {company.image?.main?.fileName && (
                            <img
                              src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/companyImages/${company.image.main.fileName}`}
                              alt={company.name}
                              style={{ width: '30px', height: '30px', marginRight: '10px' }}
                              className="rounded-circle"
                            />
                          )}
                          {company.name}
                        </div>
                      </td>
                      <td className="align-middle text-center">
                        <strong>{getCompanyInvitedTotal(company)}</strong>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-2 align-items-center">
                          {getCompanyCodes(company.id).map((code) => (
                            <div key={code.id} className="border rounded px-2 py-1 d-flex align-items-center">
                              <span className="me-2">{code.code}</span>
                              <span className="me-2 text-muted">({codesUsage[code.code] || 0})</span>
                              {code.discountCode ? (
                                <>
                                  <MDBBadge
                                    color="success"
                                    className="me-2 cursor-pointer"
                                    title={`${code.discountCode.usageCount || 0}/${code.discountCode.maxUsage} ${t("used")} - ${t("Click to edit")}`}
                                    onClick={() => {
                                      setDiscountForm({ maxUsage: code.discountCode.maxUsage });
                                      setDiscountModal({
                                        open: true,
                                        editMode: true,
                                        discountCodeId: code.discountCode.id,
                                        invitationCodeId: code.id,
                                        companyId: company.id,
                                        companyName: company.name,
                                        code: code.code
                                      });
                                    }}
                                  >
                                    <MDBIcon fas icon="gift" className="me-1" />
                                    {code.discountCode.usageCount || 0}/{code.discountCode.maxUsage}
                                  </MDBBadge>
                                  <MDBIcon
                                    fas
                                    icon="unlink"
                                    className="text-warning cursor-pointer me-2"
                                    title={t("Unlink discount")}
                                    onClick={() => handleUnlinkDiscount(company.id, code.id, code.discountCode.id)}
                                  />
                                </>
                              ) : (
                                <MDBIcon
                                  fas
                                  icon="percentage"
                                  className="text-primary cursor-pointer me-2"
                                  title={t("Link discount code")}
                                  onClick={() => setDiscountModal({
                                    open: true,
                                    invitationCodeId: code.id,
                                    companyId: company.id,
                                    companyName: company.name,
                                    code: code.code
                                  })}
                                />
                              )}
                              <MDBIcon
                                fas
                                icon="times"
                                className="text-danger cursor-pointer"
                                onClick={() => handleDeleteCode(company.id, code.id)}
                              />
                            </div>
                          ))}
                          {Object.keys(newCodes).includes(company.id) ? (
                            <MDBRow className="align-items-center g-2">
                              <MDBCol size="auto">
                                <MDBInput
                                  type="text"
                                  value={newCodes[company.id]}
                                  onChange={(e) => setNewCodes(prev => ({
                                    ...prev,
                                    [company.id]: e.target.value
                                  }))}
                                  className="m-0"
                                  style={{ width: '150px' }}
                                />
                              </MDBCol>
                              <MDBCol size="auto">
                                <MDBIcon
                                  fas
                                  icon="check"
                                  className="text-success cursor-pointer"
                                  onClick={() => handleAddCode(company.id, newCodes[company.id])}
                                />
                               
                              </MDBCol>
                            </MDBRow>
                          ) : <>
                            <MDBIcon
                              fas
                              icon="plus"
                              className="cursor-pointer"
                              onClick={() => setNewCodes(prev => ({...prev, [company.id]: ''}))}
                            />
                            <MDBIcon
                            fas
                            icon="magic"
                            className="text-success cursor-pointer"
                            onClick={() => generateAndAddCodes(company.id)}
                             />
                             </>
                          }
                        </div>
                      </td>
                    </tr>
                  ))}
                </MDBTableBody>
              </MDBTable>
            )}
          </MDBTabsPane>
        </MDBTabsContent>
      </MDBCardBody>

      {/* Discount Link Modal */}
      <MDBModal open={discountModal.open} onClose={() => setDiscountModal({ open: false, editMode: false, discountCodeId: null, invitationCodeId: null, companyId: null, companyName: '', code: '' })}>
        <MDBModalDialog>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>{discountModal.editMode ? t("Edit Discount Code") : t("Link Discount Code")}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={() => setDiscountModal({ open: false, editMode: false, discountCodeId: null, invitationCodeId: null, companyId: null, companyName: '', code: '' })}></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
              <p>
                {discountModal.editMode
                  ? <>{t("Edit discount code for invitation code")} <strong>{discountModal.code}</strong> ({discountModal.companyName})</>
                  : <>{t("Create a 100% discount code for invitation code")} <strong>{discountModal.code}</strong> ({discountModal.companyName})</>
                }
              </p>
              <MDBInput
                label={t("Max. free tickets")}
                type="number"
                min={1}
                value={discountForm.maxUsage}
                onChange={(e) => setDiscountForm({ ...discountForm, maxUsage: parseInt(e.target.value) || 1 })}
                className="mb-3"
              />
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" onClick={() => setDiscountModal({ open: false, editMode: false, discountCodeId: null, invitationCodeId: null, companyId: null, companyName: '', code: '' })}>
                {t("Cancel")}
              </MDBBtn>
              <MDBBtn color="success" onClick={discountModal.editMode ? handleUpdateDiscount : handleLinkDiscount} disabled={isSavingDiscount}>
                {isSavingDiscount ? <MDBSpinner size="sm" /> : <><MDBIcon fas icon={discountModal.editMode ? "save" : "gift"} className="me-2" />{discountModal.editMode ? t("Save") : t("Create & Link")}</>}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </MDBCard>
  );
}